import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString([], {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function StudentUnionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const government = data?.government || null;
  const constitution = data?.constitution || null;
  const nextElection = data?.nextElection || null;

  useEffect(() => {
    async function loadStudentUnion() {
      try {
        const res = await API.get('/student-union');
        setData(res.data.data);
      } catch (err) {
        console.error(err);
        setMessage('Unable to load Student Union information right now.');
      } finally {
        setLoading(false);
      }
    }

    loadStudentUnion();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-green-700">Student Leadership</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-950 sm:text-4xl">Student Union</h1>
          <p className="mt-3 max-w-3xl text-gray-600">
            Meet the elected student representatives and access the governing constitution.
          </p>
        </div>

        {message && (
          <div className="mb-6 rounded border border-red-100 bg-white px-4 py-3 text-sm text-red-700 shadow-sm">
            {message}
          </div>
        )}

        {loading ? (
          <div className="rounded bg-white p-6 shadow-sm">Loading Student Union information...</div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-6">
              {government?.members?.length ? (
                <section className="rounded bg-white p-6 shadow-sm">
                  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-950">{government.name}</h2>
                      {(government.term_start || government.term_end) && (
                        <p className="mt-1 text-sm text-gray-600">
                          {government.term_start ? formatDate(government.term_start) : 'Current'} - {government.term_end ? formatDate(government.term_end) : 'Present'}
                        </p>
                      )}
                    </div>
                    {nextElection && (
                      <Link to="/elections" className="rounded bg-green-600 px-4 py-2 text-center font-semibold text-white hover:bg-green-700">
                        View Elections
                      </Link>
                    )}
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    {government.members.map((member) => (
                      <div key={member.id} className="rounded border border-gray-200 p-4">
                        <div className="flex gap-4">
                          <div className="h-20 w-20 shrink-0 overflow-hidden rounded bg-gray-100">
                            {member.profile_picture ? (
                              <img
                                src={`${API_BASE_URL}${member.profile_picture}`}
                                alt={member.student_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-gray-400">
                                {member.student_name?.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-950">{member.student_name}</h3>
                            <p className="mt-1 text-sm font-semibold text-green-700">{member.position}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
                <section className="rounded bg-white p-6 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-950">No Current Government Yet</h2>
                  {nextElection ? (
                    <>
                      <p className="mt-3 text-gray-700">
                        The next Student Union election is scheduled for {formatDateTime(nextElection.start_at)}.
                      </p>
                      <Link
                        to="/elections"
                        className="mt-5 inline-flex rounded bg-green-600 px-5 py-2.5 font-semibold text-white hover:bg-green-700"
                      >
                        View Election and Vote
                      </Link>
                    </>
                  ) : (
                    <p className="mt-3 text-gray-700">No Student Union election has been published yet.</p>
                  )}
                </section>
              )}
            </div>

            <aside className="space-y-6">
              <section className="rounded bg-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-950">Student Constitution</h2>
                {constitution?.file_path ? (
                  <>
                    <p className="mt-2 text-sm text-gray-600">{constitution.title}</p>
                    <a
                      href={`${API_BASE_URL}${constitution.file_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex rounded border border-green-600 px-4 py-2 font-semibold text-green-700 hover:bg-green-50"
                    >
                      Open Constitution
                    </a>
                  </>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">The Student Union constitution has not been uploaded yet.</p>
                )}
              </section>

              {government?.members?.length && nextElection ? (
                <section className="rounded border border-green-100 bg-green-50 p-6">
                  <h2 className="text-xl font-bold text-gray-950">Upcoming Election</h2>
                  <p className="mt-2 text-sm text-gray-700">
                    {nextElection.title} opens on {formatDateTime(nextElection.start_at)}.
                  </p>
                  <Link
                    to="/elections"
                    className="mt-4 inline-flex rounded bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700"
                  >
                    Go to Elections
                  </Link>
                </section>
              ) : null}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
